/**
 * Der HTTP-Vertrag des Core.
 *
 * Aus diesen Schemata erzeugt Fastify zur Laufzeit die Validierung **und** die
 * OpenAPI-Beschreibung, und der Client zieht daraus seine Typen. Eine
 * Abweichung zwischen Server und Client faellt damit beim Uebersetzen auf und
 * nicht im Einsatz.
 */
import { z } from "zod";
import { Id, Zeitpunkt } from "./basis.js";
import { ArtikelMitZustand } from "./artikel.js";
import { Buchung, BuchungFelder } from "./buchung.js";
import { Einsatzstelle } from "./einsatzstelle.js";
import { Status, Verbleib } from "./enums.js";
import { Lagerort } from "./lagerort.js";
import { Benutzer } from "./stammdaten.js";
import { StatusArt } from "./statusart.js";
/** Einheitliche Fehlerantwort. Die Meldung ist fuer Menschen gedacht. */
export const Fehler = z.object({
    fehler: z.string(),
    /** Feldbezogene Hinweise aus der Vertragspruefung. */
    felder: z.array(z.object({ pfad: z.string(), meldung: z.string() })).default([]),
});
export const Gesundheit = z.object({
    ok: z.boolean(),
    core_api: z.string(),
    datenbank: z.boolean(),
});
// --- Buchungen -------------------------------------------------------------
/**
 * Buchungen werden **stapelweise** uebertragen.
 *
 * Die Offline-Warteschlange eines Geraets enthaelt nach einem Einsatz leicht
 * dreissig Buchungen. Einzeln uebertragen hiesse dreissig Anfragen ueber eine
 * Funkverbindung, die vielleicht fuenf Sekunden haelt. Ein Stapel geht in
 * einem Zug raus, und jede Position bekommt ihre eigene Rueckmeldung.
 */
export const BuchungenAnlegenAnfrage = z.object({
    // `BuchungFelder` und nicht `BuchungAnlegen`: Die Fachregeln greifen je
    // Position im Core-Dienst, damit eine fehlerhafte Buchung die uebrigen
    // neunundzwanzig nicht mit sich reisst.
    buchungen: z.array(BuchungFelder).min(1).max(500),
});
export const BuchungErgebnis = z.discriminatedUnion("ergebnis", [
    z.object({
        ergebnis: z.literal("angelegt"),
        id: Id,
        folge: z.number().int().positive(),
    }),
    /**
     * Die Buchung war schon da -- dieselbe `id` wurde erneut uebertragen.
     *
     * Das ist der Normalfall bei wackliger Verbindung und **kein Fehler**: Der
     * Client hat gesendet, die Antwort ging verloren, er sendet erneut. Weil die
     * `id` auf dem Geraet entsteht, erkennt der Server das und zaehlt nicht
     * doppelt (CLAUDE.md §3.2).
     */
    z.object({
        ergebnis: z.literal("bereits_vorhanden"),
        id: Id,
        folge: z.number().int().positive(),
    }),
    z.object({
        ergebnis: z.literal("abgelehnt"),
        id: Id,
        grund: z.string(),
    }),
]);
export const BuchungenAnlegenAntwort = z.object({
    ergebnisse: z.array(BuchungErgebnis),
    /** Hoechste vergebene Folgenummer -- taugt direkt als naechster Cursor. */
    folge_stand: z.number().int().nonnegative(),
});
/**
 * Abgleich in Gegenrichtung: alles, was seit einer Folgenummer dazugekommen
 * ist. Der Client merkt sich `folge_stand` und fragt beim naechsten Mal damit
 * weiter.
 */
export const BuchungenLesenAnfrage = z.object({
    seit_folge: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce.number().int().positive().max(1000).default(200),
    artikel_id: Id.optional(),
});
export const BuchungenLesenAntwort = z.object({
    buchungen: z.array(Buchung),
    folge_stand: z.number().int().nonnegative(),
    /** `true`, solange noch mehr abzuholen ist. */
    weitere: z.boolean(),
});
// --- Artikel ---------------------------------------------------------------
export const ArtikelSucheAnfrage = z.object({
    /** Freitext ueber Bezeichnung, Inventarnummer und Sachnummer. */
    suche: z.string().trim().max(200).optional(),
    status: Status.optional(),
    verbleib: Verbleib.optional(),
    lagerort_id: Id.optional(),
    einsatzstelle_id: Id.optional(),
    /** Archivierte sind standardmaessig ausgeblendet (KONZEPT.md §7.2). */
    mit_archivierten: z.stringbool().default(false),
    limit: z.coerce.number().int().positive().max(500).default(50),
    versatz: z.coerce.number().int().nonnegative().default(0),
});
export const ArtikelListe = z.object({
    artikel: z.array(ArtikelMitZustand),
    gesamt: z.number().int().nonnegative(),
});
export const ArtikelHistorie = z.object({
    artikel: ArtikelMitZustand,
    buchungen: z.array(Buchung),
});
// --- Etiketten --------------------------------------------------------------
/**
 * Etiketten fuer Lagerorte als PDF (KONZEPT.md §5.3).
 *
 * - `bogen`: DIN A4, 3 × 8 selbstklebende Etiketten.
 * - `a4`: ein Lagerort je Seite, QR gross -- zum Aufhaengen an Halle, Regal
 *   oder Fahrzeug, lesbar aus einigen Metern.
 *
 * Etiketten fuer den Labelmaker entstehen im Browser, nicht hier: Sie sollen
 * auch an der Einsatzstelle ohne Netz gedruckt werden koennen.
 */
export const EtikettenAnfrage = z.object({
    lagerort_ids: z.array(Id).min(1).max(500),
    format: z.enum(["bogen", "a4"]).default("bogen"),
    /**
     * Bei Packstuecken „Stammplatz: …“ mitdrucken (ADR 0013 §12). Er steht dann
     * statt des uebergeordneten Orts, der fuer ein Packstueck nichts sagt -- es
     * haengt, wo es gebucht ist. Seit Vertrag 2.8.
     */
    stammplatz: z.boolean().default(true),
});
// --- Status -----------------------------------------------------------------
/** Alle Status, auch stillgelegte -- alte Buchungen tragen sie weiter. */
export const StatusArtenListe = z.object({
    status_arten: z.array(StatusArt),
});
// --- Abgleich --------------------------------------------------------------
/**
 * Ein Benutzer, wie ihn jedes Geraet kennt: nur, was die Historie zum Lesen
 * braucht (KONZEPT.md §17, Datensparsamkeit). Rolle, Benutzername und
 * Zustand des Kontos bleiben in der Verwaltung.
 */
export const BenutzerKurz = Benutzer.pick({ id: true, anzeigename: true });
/**
 * Sammelabruf fuer den Offline-Betrieb: der **vollstaendige** Stammstand
 * (KONZEPT.md §15.1).
 *
 * Die Buchungen gehoeren nicht hinein. Sie holt das Geraet getrennt und
 * schrittweise ueber `GET /api/core/buchungen?seit_folge=…` und rechnet den
 * Zustand daraus selbst -- mit derselben Ableitung wie der Server. Der
 * mitgelieferte `zustand` jedes Artikels ist der Lesecache des Servers zum
 * Zeitpunkt `stand`.
 *
 * Seit Vertrag 2.3 mit Lagerorten, Einsatzstellen, Benutzern, Status und der
 * oeffentlichen Adresse -- und ohne Obergrenze bei den Artikeln.
 */
export const AbgleichAntwort = z.object({
    /** Uhrzeit des Servers. Daran misst das Geraet, ob seine Uhr stimmt (§15.6). */
    stand: Zeitpunkt,
    folge_stand: z.number().int().nonnegative(),
    artikel: z.array(ArtikelMitZustand),
    /** Alle Lagerorte, auch stillgelegte -- alte Buchungen zeigen auf sie. */
    lagerorte: z.array(Lagerort),
    einsatzstellen: z.array(Einsatzstelle),
    benutzer: z.array(BenutzerKurz),
    /** Alle Status, auch stillgelegte. */
    status_arten: z.array(StatusArt),
    /** Die oeffentliche Adresse fuer Lagerort-QR-Codes (ADR 0008). */
    basis_adresse: z.string(),
});
//# sourceMappingURL=api.js.map